import React from 'react';
import isValid from 'date-fns/isValid';
import parseISO from 'date-fns/parseISO';
import addMinutes from 'date-fns/addMinutes';
import { defineMessages } from 'react-intl-next';

import { RovoIcon } from '@atlaskit/logo';
import { Box } from '@atlaskit/primitives/compiled';

import { buildEditorFlagMessage } from '@confluence/editor-flags/entry-points/buildEditorFlagMessage.ts';
import { ALLOWED_FLAG_MESSAGES } from '@confluence/editor-flags/entry-points/allowedFlagConstants.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { RestrictionsRESTObj } from '@confluence/restrictions/entry-points/RestrictionsREST.ts';
import {
	transformUserToREST,
	transformGroupToREST,
} from '@confluence/restrictions/entry-points/RestrictionsREST.ts';
import type { GqlRestrictionsInput } from '@confluence/restrictions/entry-points/convertRestrictionsToGqlRestrictionsInput.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { type FragmentWriterInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';

import { PublishErrorFlagKeys } from './constants.ts';
import type { PublishModalWithMoreOptionsQuery_singleContent_Content_schedulePublishInfo_SchedulePublishInfo_restrictions_ScheduledRestrictions as ScheduledRestrictionsType } from './__types__/PublishModalWithMoreOptionsQuery.ts';

export function getWindowDimensions() {
	const hasWindow = typeof window !== 'undefined';
	const width = hasWindow ? window.innerWidth : null;
	const height = hasWindow ? window.innerHeight : null;
	return { width, height };
}

const ALL_TIMES_ARRAY = Array(48)
	.fill(0)
	.map(
		(_, i) =>
			`${Math.floor(i / 2)
				.toString()
				.padStart(2, '0')}:${i % 2 ? '30' : '00'}`,
	);

const i18n = defineMessages({
	acceptTitle: {
		id: 'publish-modal.flag.accept',
		defaultMessage: 'Accept',
		description: 'Button to accept the new title and publish page',
	},
});

// Utility function for getting time array for schedule publish timepicker
export const getTimesArray = (scheduledDate: string | undefined): string[] => {
	const currTime = new Date();
	const baseTime = addMinutes(currTime, 5);

	// Return empty array when it is already 5 minutes until tomorrow
	const tomorrow = new Date();
	tomorrow.setDate(tomorrow.getDate() + 1);
	if (baseTime.toDateString() === tomorrow.toDateString()) {
		return [];
	}

	// Return array of valid time selections
	if (!!scheduledDate && parseISO(scheduledDate).toDateString() === baseTime.toDateString()) {
		// User selected same day, only return time in the future
		const baseHour = baseTime.getHours();
		const nextHour = baseHour + 1;
		// Time array
		const timesArray = Array(48 - nextHour * 2)
			.fill(0)
			.map(
				(_, i) =>
					`${Math.floor((i + nextHour * 2) / 2)
						.toString()
						.padStart(2, '0')}:${i % 2 ? '30' : '00'}`,
			);
		if (baseTime.getMinutes() < 30) {
			// If current time is in first half of the hour, add {currentHour:30} to the time array
			timesArray.unshift(`${baseHour.toString().padStart(2, '0')}:30`);
		}
		return timesArray;
	} else {
		// User selected future day, return all times array
		return ALL_TIMES_ARRAY;
	}
};

export const mergeDateTime = (date: Date | null, time: Date | null): Date | undefined => {
	return date && time && isValid(date) && isValid(time)
		? new Date(
				date.getFullYear(),
				date.getMonth(),
				date.getDate(),
				time.getHours(),
				time.getMinutes(),
				time.getSeconds(),
			)
		: undefined;
};

type CacheSchedulePublishTimeParams = {
	contentId: string;
	schedulePublishDate: string | null;
	fragmentWriterInterop: FragmentWriterInterop<any>;
};
// Adds the provided schedulePublishInfo.date to the Apollo cache for the content identified by contentId.
// This can be used to temporarily specify the schedulePublishDate the publish modal will use
// before it finishes re-querying the server with the `PublishModalWithMoreOptionsQuery`.
export const cacheSchedulePublishTime = ({
	contentId,
	schedulePublishDate,
	fragmentWriterInterop,
}: CacheSchedulePublishTimeParams) => {
	try {
		fragmentWriterInterop({
			fragmentId: `Content:${contentId}`,
			data: {
				schedulePublishInfo: {
					__typename: 'SchedulePublishInfo',
					date: schedulePublishDate,
					minorEdit: false,
					targetType: 'page',
					versionComment: '',
				},
				__typename: 'Content',
			},
		});
	} catch {}
};

type displayPublishErrorFlagType = {
	validationMessage: string;
	isTitleEmpty: boolean;
	flags: FlagsStateContainer;
	formatMessage: (...args: any[]) => any;
	publish: () => Promise<void>;
	isAIEnabledForContent: boolean;
	isLivePage: boolean;
};
export const displayPublishErrorFlag = async ({
	validationMessage,
	isTitleEmpty,
	flags,
	formatMessage,
	publish,
	isAIEnabledForContent,
	isLivePage,
}: displayPublishErrorFlagType) => {
	let flagErrorId;
	// Will show a generic error flag if these three conditions not met
	// The validation query results do not differentiate b/w blank and whitespace title so we use isTitleEmpty to do so on the frontend side
	if (
		isTitleEmpty &&
		validationMessage === PublishErrorFlagKeys.TITLE_IS_BLANK &&
		isAIEnabledForContent &&
		!isLivePage &&
		expValEquals('confluence_ai_title_generation', 'cohort', 'test')
	)
		flagErrorId = ALLOWED_FLAG_MESSAGES.AI_GENERATED_TITLE;
	else if (isTitleEmpty && validationMessage === PublishErrorFlagKeys.TITLE_IS_BLANK)
		flagErrorId = ALLOWED_FLAG_MESSAGES.TITLE_REQUIRED;
	else if (validationMessage === PublishErrorFlagKeys.TITLE_IS_BLANK)
		flagErrorId = ALLOWED_FLAG_MESSAGES.INVALID_PAGE_TITLE;
	else if (validationMessage === PublishErrorFlagKeys.DUPLICATE_TITLE)
		flagErrorId = ALLOWED_FLAG_MESSAGES.DUPLICATE_TITLE;

	const flagMessage = buildEditorFlagMessage({
		errorData: { [flagErrorId]: true },
		formatMessage,
	});

	if (flagErrorId === ALLOWED_FLAG_MESSAGES.AI_GENERATED_TITLE) {
		await flags.hideFlag('ai-generated-title-flag');
		await flags.showCustomFlag({
			id: 'ai-generated-title-flag',
			title: flagMessage?.title,
			description: flagMessage?.description as React.ReactNode,
			customIcon: (
				<Box paddingBlockStart="space.050">
					<RovoIcon label="" size="xsmall" />
				</Box>
			),
			actions: [
				{
					content: formatMessage(i18n.acceptTitle),
					onClick: publish,
				},
			],
		});
	} else {
		await flags.showErrorFlag({
			title: flagMessage?.title,
			description: flagMessage?.description as React.ReactNode,
		});
	}
};

/**
 * Currently publish modal cares about 3 kinds of restrictions objects (unfortunately they all exist on the backend):
 * 1. Restrictions that come from content graphql -> restrictions key - RestrictionsRESTQuery (from restrictions dialog), which has type RestrictionsRESTQueryType,
 *  and converted to RestrictionsRESTObj by transformGraphQLQueryToRESTObj inside next/packages/restrictions/src/RestrictionsREST/restrictionsRESTUtils.ts
 * 2. Restrictions that come from content graphql -> schedulePublishInfo -> restrictions key - PublishModalWithMoreOptionsQuery, which has type ScheduledRestrictionsType
 * 3. Restrictions for GraphQL mutations, which has type GqlRestrictionsInput
 *
 * Function convertRestrictionsObjFromSchedulePublishInfo is to convert ScheduledRestrictionsType to RestrictionsRESTObj (format 2 to format 1)
 */
export const getRestrictionsForPublish = (
	gqlRestrictionsInput?: GqlRestrictionsInput | undefined,
): GqlRestrictionsInput | undefined => {
	if (!gqlRestrictionsInput) {
		return undefined;
	}
	return gqlRestrictionsInput;
};

export const convertRestrictionsObjFromSchedulePublishInfo = (
	scheduledRestrictions: ScheduledRestrictionsType,
): RestrictionsRESTObj => {
	const readRestriction = scheduledRestrictions?.read;
	const updateRestriction = scheduledRestrictions?.update;

	const usersWithReadRestriction = readRestriction?.user?.edges ?? [];
	const groupsWithReadRestriction = readRestriction?.group?.edges ?? [];
	const usersWithUpdateRestriction = updateRestriction?.user?.edges ?? [];
	const groupsWithUpdateRestriction = updateRestriction?.group?.edges ?? [];

	const processUserEdgesArray = (userEdgesArray) =>
		userEdgesArray.map((element) =>
			transformUserToREST({
				...element.node,
				hasSpaceEditPermission: true,
				hasSpaceViewPermission: true,
			}),
		);

	const processGroupEdgesArray = (groupEdgesArray) =>
		groupEdgesArray.map((element) => transformGroupToREST(element.node));

	const createRestrictionsObject = (operation: 'read' | 'update', users, groups) => ({
		operation,
		restrictions: {
			user: { results: processUserEdgesArray(users) },
			group: { results: processGroupEdgesArray(groups) },
		},
	});

	const readObj = createRestrictionsObject(
		'read',
		usersWithReadRestriction,
		groupsWithReadRestriction,
	);
	const updateObj = createRestrictionsObject(
		'update',
		usersWithUpdateRestriction,
		groupsWithUpdateRestriction,
	);

	return [readObj, updateObj];
};

/**
 * Converts restrictions from SchedulePublishInfo format to GqlRestrictionsInput format.
 * This is used when 'use_graphql_to_publish_pages_and_blogs' experiment is enabled.
 * Will replace convertRestrictionsObjFromSchedulePublishInfo when experiment is cleaned up.
 */
export const convertGqlRestrictionsInputFromSchedulePublishInfo = (
	scheduledRestrictions: ScheduledRestrictionsType,
): GqlRestrictionsInput => {
	const readRestriction = scheduledRestrictions?.read;
	const updateRestriction = scheduledRestrictions?.update;

	const usersWithReadRestriction = readRestriction?.user?.edges ?? [];
	const groupsWithReadRestriction = readRestriction?.group?.edges ?? [];
	const usersWithUpdateRestriction = updateRestriction?.user?.edges ?? [];
	const groupsWithUpdateRestriction = updateRestriction?.group?.edges ?? [];

	return {
		read: {
			user: usersWithReadRestriction
				.filter((edge): edge is NonNullable<typeof edge> => edge?.node != null)
				.map((edge) => ({
					id: edge?.node?.accountId ?? '',
				})),
			group: groupsWithReadRestriction
				.filter((edge): edge is NonNullable<typeof edge> => edge?.node != null)
				.map((edge) => ({
					name: edge?.node?.name ?? '',
					id: edge?.node?.id || undefined,
				})),
		},
		update: {
			user: usersWithUpdateRestriction
				.filter((edge): edge is NonNullable<typeof edge> => edge?.node != null)
				.map((edge) => ({
					id: edge?.node?.accountId ?? '',
				})),
			group: groupsWithUpdateRestriction
				.filter((edge): edge is NonNullable<typeof edge> => edge?.node != null)
				.map((edge) => ({
					name: edge?.node?.name ?? '',
					id: edge?.node?.id || undefined,
				})),
		},
	};
};
